import React, { useEffect, useRef, useState } from 'react'
import '../styles/MainContainer.css'
import ContextMenu from '../context-menu/ContextMenu'
import ContextMenuItem from '../context-menu/ContextMenuItem'
import { useContextMenu } from '../context/ContextMenuContext'
import '../popup/EditPopup.css'
import '../form/Form.css'
import CustomComboBox from '../input/CustomComboBox'
import './Wiki.css'
import api from '../wrapper/API'
import { useSuccessBar } from '../context/SuccessBarContext'
import Popup from '../popup/Popup'
import { usePopup } from '../context/PopupContext'
import WikiListItem from '../list/WikiListItem'
import ListHeader from '../list/ListHeader'
import ListTextItem from '../list/ListTextItem'
import OfflineBanner from '../shell/OfflineBanner'
import { useOffline } from '../context/OfflineContext'
import { wikiPriorityOptions } from './WikiPriorityOptions'
import { removeWikiEntry, upsertWikiEntry } from '../../hooks/useWikis'
import {
  validateWikiEntry, wikiApiErrorMessage, WIKI_ENTRY_TITLE_MAX, WIKI_PRIORITY_IMPORTANT, WIKI_PRIORITY_NORMAL,
} from '../../util/wikis'

const CONTEXT_MENU_TYPE = 'wikientry';
const EDIT_POPUP = 'editwikientry';
const DELETE_POPUP = 'confirmdeletewikientry';
const ERROR_POPUP = 'wikientryerror';

/**
 * List column of a wiki page (/wiki/:id): header slot (wiki name + actions), then the entries. Admins edit an
 * entry by click (popup) and edit/delete it via long press / right click; the changes patch the shared wiki
 * list locally (useWikis). The Prio column only appears when at least one entry is marked "wichtig".
 */
function WikiList({ wikiPageId, entries, header = null, canEdit = false }) {
  const {startSuccessBarLoading, startSuccessBarSuccess, closeSuccessBar} = useSuccessBar();
  const { isPopupOpen, popupData, openPopup, closePopup } = usePopup();
  const { contextMenuData } = useContextMenu();
  const { isOffline } = useOffline();

  const withPriority = entries.some(e => e.priority === WIKI_PRIORITY_IMPORTANT);

  // Delete
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleteError, setDeleteError] = useState('');

  const askDelete = (entry) => {
    if (!entry) return;
    setPendingDelete(entry);
    openPopup(DELETE_POPUP, entry.id);
  };

  const handleConfirmDelete = () => {
    const entry = pendingDelete;
    closePopup();
    if (!entry) return;
    startSuccessBarLoading("Eintrag wird gelöscht");
    api.delete(`/WikiEntry/Delete/${entry.id}`)
      .then(() => {
        removeWikiEntry(wikiPageId, entry.id);
        startSuccessBarSuccess("Eintrag gelöscht");
      })
      .catch((err) => {
        // Already gone (e.g. deleted by another admin): same result.
        if (err?.response?.status === 404) {
          removeWikiEntry(wikiPageId, entry.id);
          startSuccessBarSuccess("Eintrag gelöscht");
          return;
        }
        closeSuccessBar();
        setDeleteError(wikiApiErrorMessage(err, 'Löschen fehlgeschlagen.'));
        openPopup(ERROR_POPUP, entry.id);
      });
  };

  // Edit popup (kept open until the save succeeded, errors show inline)
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ title: '', content: '', priority: WIKI_PRIORITY_NORMAL });
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');
  const editOpen = isPopupOpen && popupData.name === EDIT_POPUP;
  const editOpenRef = useRef(editOpen);
  editOpenRef.current = editOpen;
  const editSessionRef = useRef(0);
  const editRequestRef = useRef(0);

  useEffect(() => {
    if (editOpen) editSessionRef.current += 1;
  }, [editOpen]);

  function openEditPopup(entry) {
    if (!entry) return;
    setEditing(entry);
    setEditForm({ title: entry.title ?? '', content: entry.content ?? '', priority: entry.priority });
    setEditError('');
    openPopup(EDIT_POPUP, entry.id);
  }

  const findMenuEntry = () => entries.find(e => e.id === contextMenuData.identifier);

  const setEditField = (key, value) => {
    setEditForm(prev => ({ ...prev, [key]: value }));
    setEditError('');
  };

  const handleEditConfirm = () => {
    if (editSaving || isOffline || !editing) return;
    const invalid = validateWikiEntry(editForm);
    if (invalid) {
      setEditError(invalid);
      return;
    }
    const target = editing;
    const request = ++editRequestRef.current;
    const session = editSessionRef.current;
    const stillEditing = () => editOpenRef.current && editSessionRef.current === session;
    setEditSaving(true);
    startSuccessBarLoading("Änderungen werden übernommen");
    api.put(`/WikiEntry/Update/${target.id}`, {
      title: editForm.title.trim(),
      content: editForm.content,
      priority: editForm.priority,
    })
      .then((response) => {
        upsertWikiEntry({ ...response.data, wikiPageId: response.data?.wikiPageId ?? wikiPageId });
        startSuccessBarSuccess("Änderungen übernommen");
        if (stillEditing()) closePopup();
      })
      .catch(err => {
        closeSuccessBar();
        const gone = err?.response?.status === 404;
        if (gone) removeWikiEntry(wikiPageId, target.id);
        if (!stillEditing()) return;
        setEditError(gone
          ? 'Der Eintrag existiert nicht mehr.'
          : wikiApiErrorMessage(err, 'Fehler beim Bearbeiten.'));
      })
      .finally(() => { if (editRequestRef.current === request) setEditSaving(false); });
  };

  return (
    <React.Fragment>
      <main className={entries.length > 0 ? "eventColumnMain hasPosts" : "eventColumnMain"}>
        <OfflineBanner />
        {header}
        {entries.length === 0 ? (
          <p className="wikiListEmpty">
            {canEdit
              ? 'Noch keine Einträge – lege den ersten über das Formular an.'
              : 'Dieses Wiki hat noch keine Einträge.'}
          </p>
        ) : (
          <React.Fragment>
            <ListHeader gridTemplateColumns={withPriority ? '40px 1fr 60px' : '40px 1fr'} size="small">
              <ListTextItem isHeader>Nr.</ListTextItem>
              <ListTextItem isHeader>Titel</ListTextItem>
              {withPriority && <ListTextItem isHeader>Prio</ListTextItem>}
            </ListHeader>
            {entries.map((entry, index) => (
              <WikiListItem
                key={entry.id} id={entry.id} title={entry.title} description={entry.content}
                priority={entry.priority} orderNumber={entry.orderNumber} index={index}
                withOrderNumber withPriority={withPriority}
                onClick={canEdit ? () => openEditPopup(entry) : undefined}
                contextMenuType={canEdit ? CONTEXT_MENU_TYPE : undefined}
              />
            ))}
          </React.Fragment>
        )}
      </main>
      {canEdit && (
        <ContextMenu type={CONTEXT_MENU_TYPE}>
          <ContextMenuItem label={'Bearbeiten'} icon={'edit'} type={0} onClick={() => openEditPopup(findMenuEntry())} disabled={isOffline} />
          <ContextMenuItem label={'Löschen'} icon={'delete_sweep'} type={1} onClick={() => askDelete(findMenuEntry())} disabled={isOffline} />
        </ContextMenu>
      )}
      <Popup name={DELETE_POPUP} confirmText='Löschen' headerText='Eintrag löschen'
        type="confirmaction" onConfirm={handleConfirmDelete} withCancel
      >
        <p>Soll der Eintrag <b>{pendingDelete?.title}</b> dauerhaft gelöscht werden?</p>
      </Popup>
      <Popup
        name={EDIT_POPUP}
        headerText="Eintrag bearbeiten"
        confirmText={editSaving ? 'Speichert …' : 'Übernehmen'}
        onConfirm={handleEditConfirm}
        dontCloseOnConfirm
        withCancel
        confirmDisabled={editSaving || isOffline}
        type="info"
      >
        <h3>Titel</h3>
        <input required type='text' className={"textInput"} placeholder='Titel' maxLength={WIKI_ENTRY_TITLE_MAX}
          value={editForm.title} onChange={e => setEditField('title', e.target.value)} />
        <h3>Beschreibung</h3>
        <textarea required className={"memoInput"} placeholder='Beschreibung'
          value={editForm.content} onChange={e => setEditField('content', e.target.value)}></textarea>
        <h3>Priorität</h3>
        <CustomComboBox
          selected={editForm.priority === WIKI_PRIORITY_IMPORTANT ? 1 : 0}
          onStatusChange={index => setEditField('priority', index === 1 ? WIKI_PRIORITY_IMPORTANT : WIKI_PRIORITY_NORMAL)}
        >
          {wikiPriorityOptions()}
        </CustomComboBox>
        {isOffline && (
          <div className="formOfflineHint">
            <span className="material-symbols-rounded formOfflineHintIcon" aria-hidden="true">cloud_off</span>
            Offline – Speichern nicht verfügbar
          </div>
        )}
        {editError && <div className="newError" role="alert">{editError}</div>}
      </Popup>
      <Popup name={ERROR_POPUP} confirmText='Ok' headerText='Löschen fehlgeschlagen' type="error">
        <p>{deleteError}</p>
      </Popup>
    </React.Fragment>
  )
}

export default WikiList
